// src/store/formSlice.js
import { createSlice } from "@reduxjs/toolkit";

const STORAGE_KEY = "weekly-student-assessments";

const safeParse = (v, fallback) => {
  try {
    const x = JSON.parse(v);
    return x ?? fallback;
  } catch {
    return fallback;
  }
};

const loadLessonsFromStorage = () => {
  const list = safeParse(localStorage.getItem(STORAGE_KEY) || "[]", []);
  if (!Array.isArray(list)) return [];
  // نطبعها على شكل Lesson داخل Redux
  return list.map((rec, idx) => ({
    lessonNo: rec?.lessonNo ?? idx + 1,
    savedAt: rec?.savedAt ?? null,
    info: rec?.info ?? {},
    axisIds: rec?.criteria?.axisIds ?? [],
    axisNames: rec?.criteria?.axisNames ?? [],
    students: rec?.students ?? [],
  }));
};

const initialState = {
  info: { school: "", subject: "", grade: "", title: "", date: null },
  criteria: { axisIds: [] },
  evaluations: [],

  // ✅ جديد: حفظ الدروس السابقة داخل Redux
  lessons: loadLessonsFromStorage(),
  currentLessonNo: loadLessonsFromStorage().length + 1,
};

const normalizeAxisIds = (value) => {
  if (Array.isArray(value))
    return value.map(Number).filter((n) => Number.isFinite(n) && n > 0);
  if (value == null || value === "") return [];
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? [n] : [];
};

const ensureAxisKeysForStudent = (student, axisIds) => {
  if (!student.ratingsByAxis) student.ratingsByAxis = {};
  if (!student.notesByAxis) student.notesByAxis = {};

  const keep = new Set(axisIds.map(String));

  for (const k of Object.keys(student.ratingsByAxis)) {
    if (!keep.has(k)) delete student.ratingsByAxis[k];
  }
  for (const k of Object.keys(student.notesByAxis)) {
    if (!keep.has(k)) delete student.notesByAxis[k];
  }

  for (const ax of axisIds) {
    const key = String(ax);
    if (!(key in student.ratingsByAxis)) student.ratingsByAxis[key] = "";
    if (!(key in student.notesByAxis)) student.notesByAxis[key] = "";
  }
};

const formSlice = createSlice({
  name: "form",
  initialState,
  reducers: {
    // Info
    setInfoField: (state, { payload: { field, value } }) => {
      state.info[field] = value;
    },

    // Criteria
    setAxis: (state, { payload }) => {
      const axisIds = normalizeAxisIds(payload);
      state.criteria.axisIds = axisIds;
      state.evaluations.forEach((st) => ensureAxisKeysForStudent(st, axisIds));
    },

    // Students
    loadStudents: (state, { payload: students }) => {
      const axisIds = normalizeAxisIds(state.criteria.axisIds);

      state.evaluations = (students || []).map((s, i) => {
        const st = {
          id: s.id ?? `s${i}`,
          name: s.name ?? String(s),
          ratingsByAxis: {},
          notesByAxis: {},
        };
        ensureAxisKeysForStudent(st, axisIds);
        return st;
      });
    },

    updateAxisRating: (state, { payload: { id, axisId, rating, name } }) => {
      let st = state.evaluations.find((e) => String(e.id) === String(id));
      if (!st) {
        st = { id: String(id), name: name || String(id), ratingsByAxis: {}, notesByAxis: {} };
        state.evaluations.push(st);
      }

      const axStr = String(axisId);

      if (!st.ratingsByAxis) st.ratingsByAxis = {};
      st.ratingsByAxis[axStr] = rating ?? "";
    },

    updateAxisNote: (state, { payload: { id, axisId, note } }) => {
      const st = state.evaluations.find((e) => e.id === id);
      if (!st) return;

      const ax = Number(axisId);
      if (!Number.isFinite(ax)) return;
      if (!state.criteria.axisIds.includes(ax)) return;

      if (!st.notesByAxis) st.notesByAxis = {};
      st.notesByAxis[String(ax)] = note ?? "";
    },

    // ✅ جديد: Hydrate الدروس من localStorage (لـ Refresh)
    hydrateLessonsFromStorage: (state) => {
      const lessons = loadLessonsFromStorage();
      state.lessons = lessons;
      state.currentLessonNo = lessons.length + 1;
    },

    /**
     * ✅ جديد: الانتقال لدرس جديد بدون حذف الدروس السابقة
     * payload = lessonSnapshot {lessonNo, savedAt, info, axisIds, axisNames, students}
     */
    goToNextLesson: (state, { payload }) => {
      if (payload) {
        state.lessons.push(payload);
      }

      state.currentLessonNo = state.lessons.length + 1;

      // افتح نموذج درس جديد (نفس الطلاب، لكن تفريغ التقييمات الحالية فقط)
      state.info = { school: "", subject: "", grade: "", title: "", date: null };
      state.criteria = { axisIds: [] };

      // لا تحذف الطلاب.. فقط صفّر ratings/notes
      state.evaluations.forEach((st) => {
        st.ratingsByAxis = {};
        st.notesByAxis = {};
      });
    },

    clearFormValues: (state) => {
      state.info = { school: "", subject: "", grade: "", title: "", date: null };
      state.criteria = { axisIds: [] };
      state.evaluations.forEach((st) => {
        st.ratingsByAxis = {};
        st.notesByAxis = {};
      });
    },
  },
});

export const {
  setInfoField,
  setAxis,
  loadStudents,
  updateAxisRating,
  updateAxisNote,
  hydrateLessonsFromStorage,
  goToNextLesson,
  clearFormValues,
} = formSlice.actions;

export default formSlice.reducer;
